<template>
    <div class="relative z-30">
        <!-- Sidebar -->
        <div
            id="sidebar"
            class="flex flex-col absolute z-30 left-0 top-0 transform h-screen overflow-y-scroll lg:overflow-y-auto no-scrollbar w-64 shrink-0 bg-navy-800 p-4 transition-all duration-200 ease-in-out"
        >
            <!-- Sidebar header -->
            <div class="flex justify-between items-center mb-10 pr-3 sm:px-2">
                <!-- Close button -->
                <button
                    class="lg:hidden text-gray-500 hover:text-gray-400 rounded hover:bg-navy-500 active:bg-navy-400 focus:bg-navy-500 focus:ring-2 focus:ring-slate-300/50 focus:outline-none active:outline-none focus-visible:outline-none transition-all"
                    style="
                        outline: none !important;
                        box-shadow: none !important;
                    "
                    @click.stop="isSidebarOpen = !isSidebarOpen"
                    aria-controls="sidebar"
                    :aria-expanded="isSidebarOpen"
                    v-if="isSidebarOpen"
                >
                    <span class="sr-only">Close sidebar</span>
                    <svg
                        class="w-6 h-6 fill-current"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M10.7 18.7l1.4-1.4L7.8 13H20v-2H7.8l4.3-4.3-1.4-1.4L4 12z"
                        />
                    </svg>
                </button>
                <!-- Logo -->
                <a
                    class="block text-center w-full rounded px-2 py-2 hover:bg-navy-500 active:bg-navy-400 focus:bg-navy-500 focus:ring-2 focus:ring-slate-300/50 focus:outline-none active:outline-none focus-visible:outline-none transition-all"
                    style="
                        outline: none !important;
                        box-shadow: none !important;
                    "
                    href="/dashboard"
                >
                    <img
                        src="/images/white-logo.svg"
                        class="mx-auto"
                        style="width: 110px; height: auto"
                        alt="My10X"
                    />
                </a>
            </div>

            <div class="space-y-8">
                <div>
                    <ul class="mt-3">
                        <li
                            class="px-3 py-2 rounded mb-2 last:mb-0 hover:bg-navy-500 active:bg-navy-400 focus-within:bg-navy-500 focus-within:ring-2 focus-within:ring-slate-300/50 transition-all"
                            :class="{
                                'bg-navy-600 dark:bg-slate-800': $page.url.startsWith(
                                    '/' + item.page
                                ),
                            }"
                            v-for="item in rootMenuItems.items"
                            :key="item"
                        >
                            <!-- Item without children -->
                            <Link
                                v-if="
                                    !item.children || item.children.length === 0
                                "
                                :href="item.url"
                                class="block text-gray-200 truncate transition duration-150 focus:outline-none active:outline-none focus-visible:outline-none"
                                style="
                                    outline: none !important;
                                    box-shadow: none !important;
                                "
                                :class="getNavClass(item)"
                                prefetch
                                @click="handleNavClick"
                            >
                                <div class="flex items-center">
                                    <Icon
                                        :icon="item.icon"
                                        class="w-6 h-6"
                                        :width="24"
                                        :height="24"
                                        :class="{
                                            'text-orange-600':
                                                $page.url.startsWith(
                                                    '/' + item.page
                                                ),
                                        }"
                                    />
                                    <span
                                        class="text-sm font-medium ml-3 duration-200"
                                    >
                                        {{ item.name }}
                                    </span>
                                </div>
                            </Link>
                            <!-- Item with children (expandable) -->
                            <button
                                v-if="item.children && item.children.length > 0"
                                type="button"
                                class="block w-full text-gray-200 truncate transition duration-150 focus:outline-none active:outline-none focus-visible:outline-none"
                                style="
                                    outline: none !important;
                                    box-shadow: none !important;
                                "
                                @click="item.open = !item.open"
                            >
                                <div class="flex items-center justify-between">
                                    <div class="flex items-center">
                                        <Icon
                                            :icon="item.icon"
                                            class="w-6 h-6"
                                            :width="24"
                                            :height="24"
                                            :class="{
                                                'text-orange-600':
                                                    $page.url.startsWith(
                                                        '/' + item.page
                                                    ),
                                            }"
                                        />
                                        <span
                                            class="text-sm font-medium ml-3 duration-200"
                                        >
                                            {{ item.name }}
                                        </span>
                                    </div>
                                    <div
                                        class="flex shrink-0 ml-2 duration-200"
                                    >
                                        <svg
                                            class="w-3 h-3 shrink-0 ml-1 fill-current text-gray-400"
                                            :class="{
                                                'transform rotate-180':
                                                    item.open,
                                            }"
                                            viewBox="0 0 12 12"
                                        >
                                            <path
                                                d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z"
                                            />
                                        </svg>
                                    </div>
                                </div>
                            </button>
                            <!-- Children submenu -->
                            <div
                                v-if="
                                    item.children &&
                                    item.children.length > 0 &&
                                    (item.open ||
                                        $page.url.startsWith('/' + item.page) ||
                                        item.children.some(
                                            (child) =>
                                                $page.url === child.url ||
                                                $page.url.startsWith(
                                                    child.url + '/'
                                                ) ||
                                                $page.url.startsWith(
                                                    child.url + '?'
                                                )
                                        ))
                                "
                                class="pl-6 mt-2"
                            >
                                <li
                                    v-for="child in item.children"
                                    :key="child.name"
                                    class="mb-1 last:mb-0 py-1 px-3 rounded hover:bg-navy-500 active:bg-navy-400 focus-within:bg-navy-600 focus-within:ring-1 focus-within:ring-slate-500 transition-all relative"
                                    :class="{
                                        'bg-navy-700': $page.url === child.url,
                                    }"
                                >
                                    <Link
                                        class="flex items-center justify-between gap-2 text-gray-400 transition duration-150 mb-1 focus:outline-none active:outline-none focus-visible:outline-none"
                                        :class="{
                                            'text-white':
                                                $page.url === child.url,
                                        }"
                                        style="
                                            outline: none !important;
                                            box-shadow: none !important;
                                        "
                                        :href="child.url"
                                        @click="handleNavClick"
                                    >
                                        <span
                                            class="text-sm font-medium duration-200 truncate"
                                        >
                                            {{ child.name }}
                                        </span>
                                        <span
                                            v-if="child.badge"
                                            class="flex-shrink-0 text-[10px] font-semibold leading-none px-1.5 py-0.5 rounded bg-orange-500 text-white uppercase tracking-wide"
                                        >
                                            {{ child.badge }}
                                        </span>
                                    </Link>
                                </li>
                            </div>
                        </li>
                    </ul>

                    <!-- ** RESOURCES ** -->
                    <div class="mt-8" v-if="resourcesMenuItems">
                        <h3
                            class="text-xs uppercase text-gray-500 font-semibold pl-3"
                        >
                            <span class="xl:block">Resources</span>
                        </h3>
                        <ul class="mt-3">
                            <li
                                v-for="item in resourcesMenuItems.items"
                                class="px-3 py-2 rounded mb-2 last:mb-0 hover:bg-navy-500 active:bg-navy-400 focus-within:bg-navy-500 focus-within:ring-2 focus-within:ring-slate-300/50 transition-all"
                                :class="{
                                    'bg-navy-600 dark:bg-slate-800': $page.url.startsWith(
                                        '/' + item.page
                                    ),
                                }"
                                :key="item"
                            >
                                <Link
                                    :href="item.url"
                                    class="block text-gray-200 truncate transition duration-150 focus:outline-none active:outline-none focus-visible:outline-none"
                                    style="
                                        outline: none !important;
                                        box-shadow: none !important;
                                    "
                                    v-if="item.children.length === 0"
                                >
                                    <div class="flex items-center">
                                        <Icon
                                            :icon="item.icon"
                                            class="w-6 h-6"
                                            :class="{
                                                'text-orange-600':
                                                    $page.url.startsWith(
                                                        '/' + item.page
                                                    ),
                                            }"
                                        />
                                        <span
                                            class="text-sm font-medium ml-3 duration-200"
                                        >
                                            {{ item.name }}
                                        </span>
                                    </div>
                                </Link>
                            </li>
                        </ul>
                    </div>
                    <!-- ** END RESOURCES ** -->

                    <!-- ** TOOLS ** -->
                    <div class="mt-8">
                        <h3
                            class="text-xs uppercase text-gray-500 font-semibold pl-3"
                        >
                            <span class="xl:block">Tools</span>
                        </h3>
                        <ul class="mt-3">
                            <li
                                v-for="item in toolsMenuItems.items"
                                class="px-3 py-2 rounded mb-2 last:mb-0 hover:bg-navy-500 active:bg-navy-400 focus-within:bg-navy-500 focus-within:ring-2 focus-within:ring-slate-300/50 transition-all"
                                :class="{
                                    'bg-navy-600 dark:bg-slate-800': $page.url.startsWith(
                                        '/' + item.page
                                    ),
                                }"
                                :key="item"
                            >
                                <Link
                                    :href="item.url"
                                    class="block text-gray-200 transition duration-150 focus:outline-none active:outline-none focus-visible:outline-none"
                                    style="
                                        outline: none !important;
                                        box-shadow: none !important;
                                    "
                                    :class="getNavClass(item)"
                                    v-if="item.children.length === 0"
                                    @click="handleNavClick"
                                >
                                    <div class="flex items-center">
                                        <Icon
                                            :icon="item.icon"
                                            class="w-6 h-6 shrink-0"
                                            :class="{
                                                'text-orange-600':
                                                    $page.url.startsWith(
                                                        '/' + item.page
                                                    ),
                                            }"
                                        />
                                        <span
                                            class="text-sm font-medium ml-3 duration-200"
                                        >
                                            {{ item.name }}
                                        </span>
                                    </div>
                                </Link>
                                <button
                                    type="button"
                                    class="block w-full text-gray-200 truncate transition duration-150 focus:outline-none active:outline-none focus-visible:outline-none"
                                    style="
                                        outline: none !important;
                                        box-shadow: none !important;
                                    "
                                    v-if="item.children.length > 0"
                                    @click="item.open = !item.open"
                                >
                                    <div
                                        class="flex items-center justify-between"
                                    >
                                        <div class="flex items-center">
                                            <Icon
                                                :icon="item.icon"
                                                class="w-6 h-6"
                                                :class="{
                                                    'text-orange-600':
                                                        $page.url.startsWith(
                                                            '/' + item.page
                                                        ),
                                                }"
                                            />
                                            <span
                                                class="text-sm font-medium ml-3 duration-200"
                                            >
                                                {{ item.name }}
                                            </span>
                                        </div>
                                        <div
                                            v-if="item.children.length > 0"
                                            class="flex shrink-0 ml-2 duration-200"
                                        >
                                            <svg
                                                class="w-3 h-3 shrink-0 ml-1 fill-current text-gray-400"
                                                :class="{
                                                    'transform rotate-180':
                                                        item.open,
                                                }"
                                                viewBox="0 0 12 12"
                                            >
                                                <path
                                                    d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                </button>
                                <div
                                    v-if="
                                        item.children.length > 0 &&
                                        (item.open ||
                                            $page.url.startsWith(
                                                '/' + item.page
                                            ))
                                    "
                                    class="pl-6 mt-2"
                                >
                                    <li
                                        v-for="child in item.children"
                                        :key="child.name"
                                        class="mb-1 last:mb-0 py-1 px-3 rounded hover:bg-navy-500 active:bg-navy-400 focus-within:bg-navy-600 focus-within:ring-1 focus-within:ring-slate-500 transition-all"
                                    >
                                        <Link
                                            class="block text-gray-400 transition duration-150 mb-1 focus:outline-none active:outline-none focus-visible:outline-none truncate"
                                            style="
                                                outline: none !important;
                                                box-shadow: none !important;
                                            "
                                            :href="child.url"
                                            @click="handleNavClick"
                                        >
                                            <span
                                                class="text-sm font-medium duration-200"
                                            >
                                                {{ child.name }}
                                            </span>
                                        </Link>
                                    </li>
                                </div>
                            </li>
                        </ul>
                    </div>
                    <!-- ** END TOOLS ** -->

                    <!-- ** SERVICES ** -->
                    <div class="mt-8" v-if="servicesMenuItems?.items?.length">
                        <h3
                            class="text-xs uppercase text-gray-500 font-semibold pl-3"
                        >
                            <span class="xl:block">Services</span>
                        </h3>
                        <ul class="mt-3">
                            <li
                                v-for="item in servicesMenuItems.items"
                                class="px-3 py-2 rounded mb-2 last:mb-0 hover:bg-navy-500 active:bg-navy-400 focus-within:bg-navy-500 focus-within:ring-2 focus-within:ring-slate-300/50 transition-all"
                                :class="{
                                    'bg-navy-600 dark:bg-slate-800': $page.url.startsWith(
                                        '/' + item.page
                                    ),
                                }"
                                :key="item"
                            >
                                <!-- External link (e.g. Travel Community) -->
                                <a
                                    v-if="item.link_type === 'external'"
                                    :href="item.url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="block text-gray-200 transition duration-150 focus:outline-none active:outline-none focus-visible:outline-none"
                                    style="
                                        outline: none !important;
                                        box-shadow: none !important;
                                    "
                                    @click="handleNavClick"
                                >
                                    <div
                                        class="flex items-center justify-between"
                                    >
                                        <Icon
                                            :icon="item.icon"
                                            class="w-6 h-6 shrink-0"
                                        />
                                        <span
                                            class="text-sm font-medium ml-3 mr-auto duration-200"
                                        >
                                            {{ item.name }}
                                        </span>
                                        <svg
                                            class="w-4 h-4 ml-2 text-gray-400 shrink-0"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        >
                                            <path d="M7 17L17 7" />
                                            <path d="M7 7h10v10" />
                                        </svg>
                                    </div>
                                </a>
                                <!-- Internal link -->
                                <Link
                                    v-else
                                    :href="item.url"
                                    class="block text-gray-200 transition duration-150 focus:outline-none active:outline-none focus-visible:outline-none"
                                    style="
                                        outline: none !important;
                                        box-shadow: none !important;
                                    "
                                    :class="getNavClass(item)"
                                    @click="handleNavClick"
                                >
                                    <div class="flex items-center">
                                        <Icon
                                            :icon="item.icon"
                                            class="w-6 h-6 shrink-0"
                                            :class="{
                                                'text-orange-600':
                                                    $page.url.startsWith(
                                                        '/' + item.page
                                                    ),
                                            }"
                                        />
                                        <span
                                            class="text-sm font-medium ml-3 duration-200"
                                        >
                                            {{ item.name }}
                                        </span>
                                        <span
                                            v-if="item.tag"
                                            class="ml-2 px-2 py-0.5 text-xs font-medium bg-orange text-white rounded-full"
                                        >
                                            {{ item.tag }}
                                        </span>
                                    </div>
                                </Link>
                            </li>
                        </ul>
                    </div>
                    <!-- ** END SERVICES ** -->

                    <div class="mt-8" v-if="securedMenuItems">
                        <ul class="mt-3">
                            <li
                                v-for="item in securedMenuItems.items"
                                class="px-3 py-2 rounded mb-2 last:mb-0 hover:bg-navy-500 active:bg-navy-400 focus-within:bg-navy-500 focus-within:ring-2 focus-within:ring-slate-300/50 transition-all"
                                :class="{
                                    'bg-navy-600': $page.url.startsWith(
                                        '/' + item.page
                                    ),
                                }"
                                :key="item"
                            >
                                <a
                                    :href="item.url"
                                    class="block text-gray-200 truncate transition duration-150 focus:outline-none active:outline-none focus-visible:outline-none"
                                    style="
                                        outline: none !important;
                                        box-shadow: none !important;
                                    "
                                    :class="getNavClass(item)"
                                    @click="handleNavClick"
                                >
                                    <div class="flex items-center">
                                        <Icon
                                            :icon="item.icon"
                                            class="w-6 h-6"
                                            :class="{
                                                'text-orange-600':
                                                    $page.url.startsWith(
                                                        '/' + item.page
                                                    ),
                                            }"
                                        />
                                        <span
                                            class="text-sm font-medium ml-3 duration-200"
                                        >
                                            {{ item.name }}
                                        </span>
                                    </div>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <!-- Tour CTA block -->
            <div class="w-full" v-if="showTourCta">
                <div
                    class="flex w-[224px] min-h-[48px] py-12 pb-6 flex-col items-start gap-2"
                >
                    <div
                        class="flex w-[224px] p-4 flex-col items-start gap-4 rounded-xl border border-slate-400 bg-[#334155] shadow-md"
                    >
                        <div
                            class="flex flex-col items-center gap-2 self-stretch"
                        >
                            <div class="flex items-center gap-1 self-stretch">
                                <div class="flex items-center gap-2 flex-1">
                                    <Icon
                                        icon="compass"
                                        class="w-5 h-5 text-white"
                                    />
                                    <span
                                        class="text-white text-[14px] font-semibold leading-5"
                                    >
                                        Get to know My10x
                                    </span>
                                </div>
                            </div>
                            <p
                                class="text-slate-300 text-[14px] leading-5 self-stretch"
                            >
                                Take a quick tour to discover the key features
                                to help you make the most of My10x.
                            </p>
                        </div>
                        <div class="flex items-start gap-4 self-stretch">
                            <button
                                type="button"
                                class="text-white text-[14px] font-medium leading-5"
                                @click="startTourFromCta"
                            >
                                Start tour
                            </button>
                            <button
                                type="button"
                                class="text-slate-400 text-[14px] font-medium leading-5"
                                @click.stop.prevent="openDismiss"
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-auto mb-10 py-1 flex flex-col items-start">
                <div
                    class="w-full py-2 rounded mb-2 last:mb-0 hover:bg-navy-500 active:bg-navy-400 focus-within:bg-navy-500 focus-within:ring-2 focus-within:ring-slate-300/50 transition-all"
                    :class="{ 'bg-navy-500 dark:bg-slate-800': page === 'support' }"
                >
                    <a
                        href="/support"
                        class="flex items-center w-full text-gray-200 transition duration-150 text-sm font-medium ml-3 focus:outline-none active:outline-none focus-visible:outline-none"
                        style="
                            outline: none !important;
                            box-shadow: none !important;
                        "
                    >
                        <Icon
                            icon="support"
                            class="w-6 h-6"
                            :class="{
                                'text-orange-600': page === 'support',
                            }"
                        />
                        <span class="ml-3">Help & Support</span>
                    </a>
                </div>
                <div
                    class="w-full py-2 rounded mb-2 last:mb-0 hover:bg-navy-500 active:bg-navy-400 focus-within:bg-navy-500 focus-within:ring-2 focus-within:ring-slate-300/50 transition-all"
                    :class="{ 'bg-navy-500 dark:bg-slate-800': page === 'faq' }"
                >
                    <Link
                        href="/faq"
                        class="flex items-center w-full text-gray-200 transition duration-150 text-sm font-medium ml-3 focus:outline-none active:outline-none focus-visible:outline-none"
                        style="
                            outline: none !important;
                            box-shadow: none !important;
                        "
                    >
                        <Icon
                            icon="faq"
                            class="w-6 h-6"
                            :class="{
                                'text-orange-600': page === 'faq',
                            }"
                        />
                        <span class="ml-3">FAQ</span>
                    </Link>
                </div>
            </div>

            <!-- Expand / collapse button -->
            <div
                class="pt-3 hidden lg:inline-flex xl:hidden justify-end mt-auto"
            >
                <div class="px-3 py-2">
                    <button @click.stop="isSidebarOpen = !isSidebarOpen">
                        <span class="sr-only">Expand / collapse sidebar</span>
                        <svg
                            class="w-6 h-6 fill-current sidebar-expanded:rotate-180"
                            viewBox="0 0 24 24"
                        >
                            <path
                                class="text-gray-400"
                                d="M19.586 11l-5-5L16 4.586 23.414 12 16 19.414 14.586 18l5-5H7v-2z"
                            />
                            <path class="text-gray-600" d="M3 23H1V1h2z" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    </div>
    <UiModal
        :key="dismissOpen ? 'dismiss_open' : 'dismiss_closed'"
        :is-open="dismissOpen"
        :is-teleported="true"
        title="Dismiss the tour?"
        description="If you dismiss it, you won’t be able to start the tour later."
        width="520px"
        @close="closeDismiss"
    >
        <template #footer>
            <div class="flex items-center justify-end gap-3">
                <button
                    type="button"
                    class="inline-flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
                    @click="closeDismiss"
                >
                    Keep for now
                </button>
                <button
                    type="button"
                    class="inline-flex items-center gap-2 rounded-md bg-slate-900 dark:bg-slate-100 px-4 py-2 text-sm font-medium text-white dark:text-slate-900 hover:brightness-95"
                    @click="confirmDismiss"
                >
                    Dismiss
                </button>
            </div>
        </template>
    </UiModal>
</template>

<script setup>
import { usePage, router } from '@inertiajs/vue3';
import { ref, onMounted, computed, onBeforeUnmount } from 'vue';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import { Link } from '@inertiajs/vue3';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/store/app';
import Icon from '@/components/ui/icon/icon.vue';
import { Apii } from '@/services/api.js';

const appStore = useAppStore();
const ApiService = new Apii();
const pageData = usePage();
const userData = computed(() => pageData.props.userData || {});
const tourTeaserSkipped = computed(() => !!pageData.props.tour_teaser_skipped);
const tourEligible = computed(() => {
    const u = userData.value || {};
    return (
        u.tour_teaser_eligible === true ||
        u.tour_teaser_eligible === 1 ||
        u.tour_teaser_eligible === '1'
    );
});
const onboardedTrue = computed(() => {
    return false;
});
const tourDismissed = computed(() => {
    const u = userData.value || {};
    return (
        u.tour_dismissed === true ||
        u.tour_dismissed === 1 ||
        u.tour_dismissed === '1' ||
        u.tour_dismissed === 'dismissed'
    );
});

const menuSections = computed(() => pageData.props.menuItems);
const rootMenuItems = computed(
    () =>
        menuSections.value.filter(
            (section, index) => section.section === 'root'
        )[0]
);

const toolsMenuItems = computed(
    () =>
        menuSections.value.filter(
            (section, index) => section.section === 'tools'
        )[0]
);

const securedMenuItems = computed(
    () =>
        menuSections.value.filter(
            (section, index) => section.section === 'secured'
        )[0]
);

const resourcesMenuItems = computed(
    () =>
        menuSections.value.filter(
            (section, index) => section.section === 'resources'
        )[0]
);

const servicesMenuItems = computed(
    () =>
        menuSections.value.filter(
            (section, index) => section.section === 'services'
        )[0]
);

const page = ref({});
const nav = ref({});

const { sidebarOpen, userSettings } = storeToRefs(appStore);
const showTourCta = ref(false);
const dismissOpen = ref(false);
const { setSidebarOpen } = appStore;
let winWidth = 0;

const isSidebarOpen = computed({
    get() {
        return sidebarOpen.value;
    },
    set(value) {
        setSidebarOpen(value);
    },
});

const resizedw = () => {
    winWidth = window.innerWidth;
    if (winWidth <= 1024) {
        isSidebarOpen.value = false;
    }
};

onMounted(() => {
    winWidth = window.innerWidth;
    if (winWidth <= 1024) {
        isSidebarOpen.value = false;
    }

    let dbounce;
    window.onresize = () => {
        clearTimeout(dbounce);
        dbounce = setTimeout(resizedw, 100);
    };
    window.openSidebar = () => {
        try {
            isSidebarOpen.value = true;
        } catch (_) {}
    };
    window.closeSidebar = () => {
        try {
            isSidebarOpen.value = false;
        } catch (_) {}
    };
    const onShowCta = () => {
        if (
            tourEligible.value &&
            !onboardedTrue.value &&
            !tourDismissed.value
        ) {
            showTourCta.value = true;
        }
    };
    window.addEventListener('show-tour-cta', onShowCta);
    window.showTourCta = () => {
        showTourCta.value = true;
    };
    window.__onShowTourCta = onShowCta;

    if (
        tourTeaserSkipped.value &&
        tourEligible.value &&
        !onboardedTrue.value &&
        !tourDismissed.value
    ) {
        showTourCta.value = true;
    }

    const onTourFinished = () => {
        try {
            showTourCta.value = false;
        } catch (_) {}
        try {
            delete window.__tourStartSource;
        } catch (_) {}
    };
    const onTourCancelled = () => {
        try {
            const source = window.__tourStartSource;
            if (source === 'teaser') {
                showTourCta.value = false;
                if (typeof window.showTourTeaser === 'function')
                    window.showTourTeaser();
            } else if (
                tourEligible.value &&
                !onboardedTrue.value &&
                !tourDismissed.value
            ) {
                showTourCta.value = true;
            }
        } catch (_) {
        } finally {
            try {
                delete window.__tourStartSource;
            } catch (_) {}
        }
    };
    window.addEventListener('user-tour-finished', onTourFinished);
    window.addEventListener('user-tour-cancelled', onTourCancelled);
    window.__onTourFinished = onTourFinished;
    window.__onTourCancelled = onTourCancelled;
});

onBeforeUnmount(() => {
    try {
        if (window.openSidebar) delete window.openSidebar;
        if (window.closeSidebar) delete window.closeSidebar;
        if (window.__onShowTourCta) {
            window.removeEventListener('show-tour-cta', window.__onShowTourCta);
            delete window.__onShowTourCta;
        }
        if (window.__onTourFinished) {
            window.removeEventListener(
                'user-tour-finished',
                window.__onTourFinished
            );
            delete window.__onTourFinished;
        }
        if (window.__onTourCancelled) {
            window.removeEventListener(
                'user-tour-cancelled',
                window.__onTourCancelled
            );
            delete window.__onTourCancelled;
        }
    } catch (_) {}
});

function getNavClass(item) {
    const name = (item?.name || '').toString().toLowerCase().trim();
    const page = (item?.page || '').toString().toLowerCase().trim();
    if (name === 'my wallet') return 'nav-my-wallet';
    if (
        name === '10xtravel+' ||
        name === '10xtravel plus' ||
        page === '10xtravel-plus' ||
        page === 'travel-plus'
    )
        return 'nav-10x-travel-plus';
    if (name === 'my benefits') return 'nav-card-benefits';
    if (name === '10x course') return 'nav-10x-course';
    if (name === 'my points') return 'nav-my-points';
    if (name === 'my next card') return 'nav-my-next-card';
    if (name === 'redemption tracker') return 'nav-redemption-tracker';
    return '';
}

function handleNavClick() {
    // Only close sidebar on mobile (1024px and below)
    if (window.innerWidth <= 1024) {
        isSidebarOpen.value = false;
    }
}
function startTourFromCta() {
    try {
        if (typeof window.openSidebar === 'function') window.openSidebar();
    } catch (_) {}
    try {
        if (typeof window.launchUserTour === 'function') {
            try {
                window.__tourStartSource = 'cta';
            } catch (_) {}
            window.launchUserTour();
        }
    } catch (_) {}
}

function openDismiss() {
    dismissOpen.value = true;
}
function closeDismiss() {
    dismissOpen.value = false;
}
async function confirmDismiss() {
    closeDismiss();
    showTourCta.value = false;
    try {
        await ApiService.apiCall('/tour/dismiss', 'post');
    } catch (_) {}
    try {
        router.reload({ only: ['userData', 'tour_teaser_skipped'] });
    } catch (_) {}
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
